import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Image as ChakraImage, // Alias the import
  Flex,
  VStack,
  Text,
  Button,
  useColorModeValue,
  useColorMode,
  HStack,
  IconButton
} from '@chakra-ui/react';
import { Plus, MapPin } from '@phosphor-icons/react';
import DetectionBoxes from './DetectionBoxes';
import DetectionsList from './DetectionsList';
import BoundingBoxDrawer from './BoundingBoxDrawer';
import SubLocationMapModal from './SubLocationMapModal';

const MediaViewer = ({ 
  media,
  showFeatureTags = true,
  onFeatureTagsToggle,
  isDrawingMode = false,
  onBoxDrawn,
  onBoxUpdated,
  onAddFeature,
  hideDetectionsList = false,
  onDeleteFeature,
  mediaId,
  siteId,
  onUpdateMedia,
  subLocation
}) => {
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [showBoundingBoxes, setShowBoundingBoxes] = useState(showFeatureTags);
  const [visibleFeatures, setVisibleFeatures] = useState({});
  const [imageError, setImageError] = useState(false);
  const [isSubLocationModalOpen, setIsSubLocationModalOpen] = useState(false);
  const { colorMode } = useColorMode();

  useEffect(() => {
    if (!media) return;
    
    const predictions = media.predictions || [];
    const initial = {};
    predictions.forEach(pred => {
      initial[pred.class] = true;
    });
    
    setVisibleFeatures(initial);
    setShowBoundingBoxes(true);
    setImageError(false);
  }, [media, showFeatureTags]);

  const updateSizes = () => {
    if (!imageRef.current || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const naturalWidth = imageRef.current.naturalWidth;
    const naturalHeight = imageRef.current.naturalHeight;
    const imageAspectRatio = naturalWidth / naturalHeight;

    // Calculate how the image fits in the container
    let displayWidth = rect.width;
    let displayHeight = rect.height;

    if (displayWidth / displayHeight > imageAspectRatio) {
      displayWidth = displayHeight * imageAspectRatio;
    } else {
      displayHeight = displayWidth / imageAspectRatio;
    }
    
    // Still set displaySize for bounding box calculations, but don't use it for rendering size
    setDisplaySize({
      width: Math.round(displayWidth),
      height: Math.round(displayHeight)
    });

    setNaturalSize({
      width: naturalWidth,
      height: naturalHeight
    });
  };

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setNaturalSize({ width: naturalWidth, height: naturalHeight });
    setImageError(false);
    updateSizes();
  };

  const handleImageError = () => {
    setImageError(true);
  };

  useEffect(() => {
    setShowBoundingBoxes(showFeatureTags);
  }, [showFeatureTags]);

  useEffect(() => {
    if (!containerRef.current || !imageRef.current) return;
    
    // Debounce the resize observer callback
    let timeoutId;
    const debouncedUpdateSizes = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateSizes, 200); // 200ms debounce
    };
    
    const resizeObserver = new ResizeObserver(debouncedUpdateSizes);
    
    // Only observe the container, not the image itself
    resizeObserver.observe(containerRef.current);
    
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, []);

  const handleFeatureToggle = (featureClass) => {
    setVisibleFeatures(prev => ({
      ...prev,
      [featureClass]: !prev[featureClass]
    }));
  };

  // Validate and normalize predictions
  const normalizedPredictions = (media.predictions || [])
    .filter(pred => pred.x != null && pred.y != null) // Filter out predictions with null coordinates
    .map((pred, index) => {
      const isNormalized = pred.x <= 1 && pred.y <= 1 && pred.width <= 1 && pred.height <= 1;
      
      // Debug prediction
      console.log(`MediaViewer Prediction #${index}:`, {
        original: pred,
        isNormalized
      });
      
      if (isNormalized) {
        return pred;
      }

      // Convert to normalized coordinates if needed
      const normalized = {
        ...pred,
        x: pred.x / naturalSize.width,
        y: pred.y / naturalSize.height,
        width: pred.width / naturalSize.width,
        height: pred.height / naturalSize.height
      };
      
      console.log(`MediaViewer Normalized #${index}:`, normalized);
      
      return normalized;
    });

  if (!media) return null;

  return (
    <Flex direction={{ base: "column", md: "row" }} gap={4}>
      <Box flex="1" position="relative" maxH="70vh" minHeight="200px"> {/* Added minHeight */}
        <Box
          ref={containerRef}
          position="relative"
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg={useColorModeValue('gray.50', 'gray.800')}
          borderRadius="md"
          overflow="hidden"
        >
          {imageError ? (
            <Text color="gray.500">Failed to load image</Text>
          ) : (
            // Let the inner Box take size from the Image and outer container
            <Box 
              position="relative" 
              // Remove explicit width/height based on displaySize state
              // width={displaySize.width} 
              // height={displaySize.height}
              // Add width/height 100% to fill the flex container space
              width="100%" 
              height="100%" 
            >
              {/* Replaced ChakraImage with standard img for debugging */}
              <img
                ref={imageRef}
                src={media.url}
                alt={media.name || 'Media preview'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain', // Keep contain for basic behavior
                  display: imageError ? 'none' : 'block' // Hide if error
                }}
                onLoad={handleImageLoad}
                onError={handleImageError}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              />
              {!imageError && normalizedPredictions.length > 0 && displaySize.width > 0 && (
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  width="100%"
                  height="100%"
                  pointerEvents="none"
                >
                  {/* Debug display dimensions */}
                  <Box
                    position="absolute"
                    top="5px"
                    left="5px"
                    bg="rgba(0,0,0,0.5)"
                    color="white"
                    fontSize="xs"
                    p={1}
                    borderRadius="md"
                    zIndex={10}
                    display="none" // Set to "block" to show debug info
                  >
                    Natural: {naturalSize.width}x{naturalSize.height}<br/>
                    Display: {displaySize.width}x{displaySize.height}
                  </Box>
                  
                  {showBoundingBoxes && (
                    <DetectionBoxes
                      showBoundingBoxes={true}
                      detections={normalizedPredictions}
                      displaySize={displaySize}
                      naturalSize={naturalSize}
                      visibleFeatures={visibleFeatures}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {!hideDetectionsList && (
        <Box minW={{ base: "100%", md: "300px" }} maxW={{ base: "100%", md: "300px" }}>
          <HStack spacing={2} mb={2}>
            <HStack spacing={2}>
              {onAddFeature && (
                <IconButton
                  icon={<Plus />}
                  aria-label="Add feature"
                  size="sm"
                  colorScheme="blue"
                  onClick={onAddFeature}
                />
              )}
              {onUpdateMedia && (
                <IconButton
                  icon={<MapPin />}
                  aria-label="Tag location"
                  size="sm"
                  colorScheme="green"
                  onClick={() => setIsSubLocationModalOpen(true)}
                />
              )}
            </HStack>
          </HStack>
          
          <VStack spacing={4} align="stretch">
            {normalizedPredictions.length > 0 ? (
              <DetectionsList
                detections={normalizedPredictions}
                showBoundingBoxes={showBoundingBoxes}
                setShowBoundingBoxes={(checked) => {
                  setShowBoundingBoxes(checked);
                  if (onFeatureTagsToggle) {
                    onFeatureTagsToggle(checked);
                  }
                }}
                visibleFeatures={visibleFeatures}
                onFeatureToggle={handleFeatureToggle}
                onDeleteFeature={onDeleteFeature}
                mediaId={mediaId}
                siteId={siteId}
              />
            ) : (
              <Box 
                p={4} 
                borderWidth="1px" 
                borderRadius="md" 
                bg={useColorModeValue('gray.50', 'gray.700')}
              >
                <Text color="gray.500" textAlign="center">
                  No features detected in this image
                </Text>
              </Box>
            )}

            {subLocation && (
              <Box p={3} borderWidth="1px" borderRadius="md">
                <Text fontWeight="bold" mb={1}>Current Location</Text>
                <Text mb={1}>{subLocation.name}</Text>
                {subLocation.coordinates && (
                  <>
                    <Text fontSize="sm" color="gray.500" mb={2}>
                      {subLocation.coordinates.lat.toFixed(6)}, {subLocation.coordinates.lng.toFixed(6)}
                    </Text>
                    <Button
                      size="sm"
                      width="full"
                      leftIcon={<MapPin />}
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => {
                        const siteUrl = `/site/${siteId}#map`;
                        window.open(siteUrl, '_blank');
                      }}
                    >
                      View on Map
                    </Button>
                  </>
                )}
              </Box>
            )}
          </VStack>

          <SubLocationMapModal
            isOpen={isSubLocationModalOpen}
            onClose={() => setIsSubLocationModalOpen(false)}
            siteId={siteId}
            mediaId={mediaId}
            currentSubLocation={subLocation}
            siteLocation={media.siteLocation}
            onSubLocationSelect={async (location) => {
              if (onUpdateMedia) {
                await onUpdateMedia({
                  subLocationId: location.id,
                  coordinates: location.coordinates
                });
              }
            }}
          />
        </Box>
      )}
    </Flex>
  );
};

export default MediaViewer;
